// Классы активностей IMU самосвала и общие хелперы для страницы самосвала.
// Кладется в src/core/pages/CameraDetailPage/utils/truckActivity.js

// Классы активностей самосвала - по идентификаторам (VehicleActivity.identifier у типа TR),
// а не по pk: на проде и на копии pk разные. Эндпоинт отдает идентификатор в timeline.class_id
// и в ключах durations_by_class_sec / shift_stats.durations.
export const ACT = {
  IDLE: "II", // Неактивность - холостой ход
  PARKED: "IO", // Неактивность - выключенный двигатель
  HAUL_LOADED: "MF", // Перемещение - груженый
  HAUL_EMPTY: "ME", // Перемещение - пустой
  UNLOAD: "AU", // Разгрузка
  LOAD: "AL", // Погрузка (удар ковша = 1 ковш)
};

// Служебные классы шкалы остаются числами: -1 нет данных, -2 ТО/выходной, -3 перерыв
export const normalizeClassId = (id) => {
  const n = Number(id);
  return Number.isFinite(n) && n < 0 ? n : String(id);
};

// Длительность активности из durations_by_class_sec / shift_stats.durations
export const durationOf = (durations, act) => {
  const value = durations ? durations[act] : null;
  return value === undefined || value === null ? 0 : Number(value);
};

export const TRUCK_CLASS_META = {
  [ACT.LOAD]: { label: "Погрузка (удар ковша)", color: "#e2572b" },
  [ACT.HAUL_LOADED]: { label: "Перемещение груженый", color: "#1f9d55" },
  [ACT.UNLOAD]: { label: "Разгрузка на отвале", color: "#8e5cc9" },
  [ACT.HAUL_EMPTY]: { label: "Перемещение пустой", color: "#3b82c4" },
  [ACT.IDLE]: { label: "Холостой ход", color: "#d99a1e" },
  [ACT.PARKED]: { label: "Стоянка (зажигание выкл.)", color: "#7b8794" },
  "-1": { label: "Нет данных", color: "#c3c9d0" },
  "-2": { label: "ТО / Выходной", color: "#7b1fa2" },
  "-3": { label: "Перерыв", color: "#00acc1" },
  default: { label: "Неизвестно", color: "#455a64" },
};

// Фазы рейса в порядке цикла: рейс начинается с концом предыдущей разгрузки.
// Подписи совпадают с ключами extra_dash_charts.trip_components.
export const PHASE_META = [
  { key: "return_empty", label: "Перемещение пустой", color: "#3b82c4" },
  { key: "idle_empty", label: "Ожидание пустой", color: "#d99a1e" },
  { key: "loading", label: "Погрузка", color: "#e2572b" },
  { key: "haul_loaded", label: "Перемещение груженый", color: "#1f9d55" },
  { key: "idle_loaded", label: "Ожидание груженый", color: "#9bd3ae" },
  { key: "unload", label: "Разгрузка", color: "#8e5cc9" },
];

export const IDLE_STATE_LABELS = {
  loaded: "Груженый",
  empty: "Пустой",
  none: "Вне рейса",
};

// Цвет рейса на таймлайне. Соседние рейсы чередуются двумя оттенками (index - порядковый
// номер рейса), чтобы на шкале была видна граница между ними. Оттенки обычного рейса - те же,
// что на ленте рейсов в окне видео.
export const TRIP_COLORS = {
  normal: ["#1f9d55", "#2e7d9a"],
  noLoading: ["#d99a1e", "#b7791f"],
  outOfShift: ["#c77b74", "#a85d57"],
};

export const tripColor = (trip, index = 0) => {
  const pair = !trip.in_stats ? TRIP_COLORS.outOfShift : !trip.loading_detected ? TRIP_COLORS.noLoading : TRIP_COLORS.normal;
  return pair[Math.abs(index) % 2];
};

// Скорость -> цвет, как пробки на Яндекс Картах: от красного (медленно) через желтый
// к светло-зеленому (быстро). t = 0..1 - доля между минимумом и максимумом шкалы.
const SPEED_STOPS = [
  [0, [229, 57, 53]],
  [0.5, [253, 216, 53]],
  [1, [124, 205, 90]],
];

export const speedColor = (t) => {
  const x = Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0));
  let i = 0;
  while (i < SPEED_STOPS.length - 2 && x > SPEED_STOPS[i + 1][0]) i += 1;
  const [x0, c0] = SPEED_STOPS[i];
  const [x1, c1] = SPEED_STOPS[i + 1];
  const k = (x - x0) / (x1 - x0);
  const rgb = c0.map((v, j) => Math.round(v + (c1[j] - v) * k));
  return `#${rgb.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

export const SPEED_GRADIENT_CSS = `linear-gradient(to right, ${speedColor(0)}, ${speedColor(0.5)}, ${speedColor(1)})`;

// Процентиль массива чисел (p = 0..100), для шкалы скорости без выбросов
export const percentile = (values, p) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = ((sorted.length - 1) * p) / 100;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
};

export const tripStatusChips = (trip) => {
  const chips = [];
  if (!trip.loading_detected) chips.push({ label: "без погрузки", color: "warning" });
  if (trip.loadings > 1) chips.push({ label: `дозагрузка ×${trip.loadings}`, color: "info" });
  if (!trip.in_stats) chips.push({ label: "вне смены", color: "danger" });
  // Поправки по привычным местам (rejected_loadings, unload_inferred, rejected_unloads из
  // truck_events.py) в таблице не показываем: пользователю они не нужны, остаются в ответе и в логе
  return chips;
};

export const fmtNum = (value, digits = 1) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? "—"
    : Number(value).toFixed(digits);

// Минимальное внешнее хранилище с подпиской. Масштаб таймлайна и время кадра видео меняются
// десятки раз в секунду; если держать их в состоянии страницы, каждое изменение перерисовывает
// всю страницу с графиками. Подписчики (таймлайн, карта) обновляются сами.
export const createStore = (initial) => {
  let value = initial;
  const subscribers = new Set();
  return {
    get: () => value,
    set: (next) => {
      const resolved = typeof next === "function" ? next(value) : next;
      if (resolved === value) return;
      value = resolved;
      subscribers.forEach((fn) => fn(value));
    },
    subscribe: (fn) => {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },
  };
};

// Индекс трека GPS для быстрого поиска точки по времени. track: [{ lat, lon, ts }] по возрастанию ts
export const buildTrackIndex = (track) => {
  const times = new Float64Array(track.length);
  for (let i = 0; i < track.length; i += 1) times[i] = new Date(track[i].ts).getTime();
  return { times, track };
};

// Положение по GPS на момент ms (линейная интерполяция между соседними точками).
// Если ближайшие точки дальше maxGapMs - GPS в этот момент не было, возвращаем null,
// чтобы не поставить отметку в случайное место.
export const positionAt = (index, ms, maxGapMs = 120000) => {
  const { times, track } = index;
  const n = times.length;
  if (!n || ms == null) return null;

  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] < ms) lo = mid + 1;
    else hi = mid;
  }
  const next = lo;
  const prev = lo - 1;

  if (next < n && times[next] === ms) return [track[next].lat, track[next].lon];
  if (prev >= 0 && next < n && times[next] - times[prev] <= maxGapMs) {
    const k = (ms - times[prev]) / (times[next] - times[prev]);
    return [
      track[prev].lat + (track[next].lat - track[prev].lat) * k,
      track[prev].lon + (track[next].lon - track[prev].lon) * k,
    ];
  }

  let best = -1;
  [prev, next].forEach((i) => {
    if (i < 0 || i >= n || Math.abs(times[i] - ms) > maxGapMs) return;
    if (best < 0 || Math.abs(times[i] - ms) < Math.abs(times[best] - ms)) best = i;
  });
  return best < 0 ? null : [track[best].lat, track[best].lon];
};

// Черный или белый текст поверх цветной плашки
export const textColorOn = (hex) => {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return "#fff";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? "#212529" : "#fff";
};
